/* tslint:disable:max-line-length */
import {Injectable, OnDestroy} from '@angular/core';
import {FaultOperationPathEnum} from '@docma-mm-core/fault/patch/fault-operation-path.enum';
import {FaultPatch} from '@docma-mm-core/fault/patch/fault-patch';
import {FaultPatchService} from '@docma-mm-core/fault/patch/fault-patch-service';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {SyncFileService} from '@docma-mm-core/sync/sync-file.service';
import {ConnectivityService} from '@mydocma/core';
import {AttachmentData} from './attachment';
import {AttachmentApiService} from './attachment-api.service';
import {mapFileEntityToFile} from './attachment-db.entity';
import {AttachmentDbService} from './attachment-db.service';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {firstValueFrom} from 'rxjs';
import {SyncResult} from '@docma-mm-core/model/sync-state/sync-result';
import {MmTranslateService} from '../../mm-translate.service';
import {ErrorService} from '../../mm-faults/mm-error.service';
import {Logger} from '@mydocma/common/src/lib/logger';
import {ScribbleData} from '@docma-mm-core/scribble/scribble';
import {ScribbleDbService} from '@docma-mm-core/scribble/scribble-db.service';
import {ScribbleApiService} from '@docma-mm-core/scribble/scribble-api.service';
import {pageBySize} from '@docma-mm-core/utils/utils';

@Injectable()
export class AttachmentSyncService implements OnDestroy {


  constructor(private readonly connectivityService: ConnectivityService,
              private readonly attachmentApiService: AttachmentApiService,
              private readonly patchService: FaultPatchService,
              private readonly syncFileService: SyncFileService,
              private readonly dbService: AttachmentDbService,
              private readonly scribbleDbService: ScribbleDbService,
              private readonly scribbleApiService: ScribbleApiService,
              private readonly notifactionService: NotificationService,
              private readonly translateService: MmTranslateService,
              private readonly errorService: ErrorService,
              private readonly logger: Logger)  {
  }
  ngOnDestroy(): void {

  }


  public async syncFaultAttachmentFromPatches(mydocmaUid: string, faultId: any, patches: FaultPatch[]): Promise<SyncResult> {
    // this.assertValidPatches(patches, FaultOperationPathEnum.attachment);

    const hasAttachmentPatches = patches.some(x => x.operation.path === FaultOperationPathEnum.attachment);

    let attachmentError: any;
    const attachmentLoadStates: LoadState<AttachmentData>[] = [];
    const scribbleErrors: any[] = [];
    const scribbleLoadStates: LoadState<ScribbleData>[] = [];

    const faultUid = patches[0].fUid;
    const attachmentUid = (hasAttachmentPatches ? patches[0].oUid : patches[0].pUid);

    try {
      const groupedScribblePatches = this.patchService.groupByOuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.attachmentScribbles));

      if (hasAttachmentPatches) {
        let attachmentChangeState: ChangeState<any>;

        console.debug(`Sync fault attachment ${faultUid}/${attachmentUid}: Starting`);

        await this.dbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, new SyncState({status: SyncStatusEnum.Syncing}));
        attachmentChangeState = this.patchService.generateChangeStateFromPatches(patches.filter(p => p.operation.path === FaultOperationPathEnum.attachment));

        try {
          if (attachmentChangeState.status !== ChangeStatusEnum.NotChanged) {
            let newFile: File;
            if (attachmentChangeState.status === ChangeStatusEnum.New) {
              newFile = await this.getAttachmentFile(mydocmaUid, attachmentUid);
              if (!newFile) {
                // NO FILE EXISTING ANYMORE; REMOVE FAULT_PATCH
                await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));

                const m = `Can't sync new attachment ${attachmentUid}. Sync file not found`;
                this.logger.error(m, '', 'AttachmentSyncService');
                throw new Error(m);
              }
            }

            const attachmentLoadedState = await this.syncFaultAttachmentChangeState(mydocmaUid, newFile, faultUid, attachmentUid, attachmentChangeState, (Object.keys(groupedScribblePatches)?.length ?? 0) > 0);
            attachmentLoadStates.push(attachmentLoadedState);
            await this.dbService.saveLoadState(mydocmaUid, faultUid, null, null, attachmentUid, attachmentLoadedState);
          }
          await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));
        } catch (err) {
          this.logger.error(err, '', 'AttachmentSyncService');

          if (err.error === 'Unique constraint violation of \'UQ_FI_UNIQUE_ID\'') {
            // ATTACHMENT ALREADY UPLOADED, REMOVE FAULT_PATCH + FILE
            try {
              await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));
              await this.syncFileService.removeFile(mydocmaUid, attachmentUid);

              try {
                const attachmentData = await this.attachmentApiService.getAttachmentData(attachmentUid);
                const attachmentLoadedState = new LoadState<AttachmentData>({status: LoadStatusEnum.Loaded, data: attachmentData});
                attachmentLoadStates.push(attachmentLoadedState);
                await this.dbService.saveLoadState(mydocmaUid, faultUid, null, null, attachmentUid, attachmentLoadedState);
              } catch (err3) {
                this.logger.error('Error loading attachment data after unique constraint violation: ' + err3, '', 'AttachmentSyncService');
              }

            } catch (err2) {
              attachmentError = this.errorService.getError(err2);
              this.logger.error(err2, '', 'AttachmentSyncService');
            }
          } else {
            attachmentError = this.errorService.getError(err);
            this.logger.error(err, '', 'AttachmentSyncService');
          }
        }
      }



      const scribbleChangeStates = Object.keys(groupedScribblePatches).map(scribbleUid => {
        const scribblePatches = groupedScribblePatches[scribbleUid];
        const scribbleChangeState = this.patchService.generateChangeStateFromPatches(scribblePatches);
        return {changeStates: scribbleChangeState, patches: scribblePatches, status: scribbleChangeState.status};
      });

      const processingScribbleUids = scribbleChangeStates.filter(x => x.changeStates.data.status !== ChangeStatusEnum.NotChanged).map(x => x.changeStates.data.UniqueId);
      await this.scribbleDbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, processingScribbleUids, new SyncState({status: SyncStatusEnum.Syncing}));

      const newScribbleChangeStatesPaged  = pageBySize<{changeStates: ChangeState<any>, patches: FaultPatch[], status: ChangeStatusEnum}>(scribbleChangeStates.filter(x => x.status === ChangeStatusEnum.New), 50);
      const updatedScribbleChangeStatesPaged  = pageBySize<{changeStates: ChangeState<any>, patches: FaultPatch[]}>(scribbleChangeStates.filter(x => x.status === ChangeStatusEnum.Changed), 50);
      const deletedScribbleChangeStatesPaged  = pageBySize<{changeStates: ChangeState<any>, patches: FaultPatch[]}>(scribbleChangeStates.filter(x => x.status === ChangeStatusEnum.Deleted), 50);

      for (const changeStates of [...newScribbleChangeStatesPaged, ...updatedScribbleChangeStatesPaged, ...deletedScribbleChangeStatesPaged]) {
        try {
          const loadedStates = await this.syncScribbleChangeStates(mydocmaUid, changeStates.map(x => x.changeStates));
          scribbleLoadStates.push(... loadedStates);
          await this.scribbleDbService.saveLoadStates(mydocmaUid, faultUid, null, null, attachmentUid, loadedStates, changeStates[0].changeStates.status === ChangeStatusEnum.Deleted);
          await this.patchService.remove(mydocmaUid, changeStates.flatMap(x => x.patches.map(p => p.operation.id)));
        } catch (err) {
          const error = this.errorService.getError(err);
          scribbleErrors.push(error);
          this.logger.error(error, '', 'AttachmentSyncService');
        }
      }

      if (attachmentError) {
        const syncStateAttachmentErr = await this.dbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Error, error: attachmentError }));
        return new SyncResult({syncState: syncStateAttachmentErr});
      } else if (scribbleErrors.length > 0) {
        const syncStateScribbleErr = await this.dbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Error, error: scribbleErrors[0] }));
        return new SyncResult({syncState: syncStateScribbleErr});
      }

      await this.scribbleDbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, processingScribbleUids, new SyncState({status: SyncStatusEnum.Synced}));

      const syncState = await this.dbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, new SyncState({status: SyncStatusEnum.Synced}));
      return new SyncResult({syncState, attachmentsLoadStates: attachmentLoadStates, attachmentScribblesLoadStates: scribbleLoadStates});
    }
    catch (err) {
      const syncStateErr = await this.dbService.saveSyncState(mydocmaUid, faultUid, null, null, attachmentUid, new SyncState({status: SyncStatusEnum.Error, error: this.errorService.getError(err)}));
      return new SyncResult({syncState: syncStateErr});
    }
  }

  public async syncActivityAttachmentFromPatches(mydocmaUid: string, newActivityId: any, patches: FaultPatch[]): Promise<({ syncResult: SyncResult, isNewAttachment: boolean })> {
    // this.assertValidPatches(patches, FaultOperationPathEnum.activityAttachment);

    const hasAttachmentPatches = patches.some(x => x.operation.path === FaultOperationPathEnum.activityAttachment);

    let attachmentError: any;
    const attachmentLoadStates: LoadState<AttachmentData>[] = [];
    const scribbleErrors: any[] = [];
    const scribbleLoadStates: LoadState<ScribbleData>[] = [];

    const attachmentId = patches[0].oId || patches[0].oUid;
    const attachmentUid = (hasAttachmentPatches ? patches[0].oUid : patches[0].pUid);
    const activityId = (patches[0].pId || patches[0].pUid) || newActivityId;
    const activityUid = (hasAttachmentPatches ? patches[0].pUid : patches[0].aUid);
    const faultUid = patches[0].fUid;
    let isNew = false;

    try {
      const groupedScribblePatches = this.patchService.groupByOuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.activityAttachmentScribbles));

      if (hasAttachmentPatches) {
        let attachmentChangeState: ChangeState<any>;

        console.debug(`Syc activity attachment ${faultUid}/${attachmentId}: Starting`);

        await this.dbService.saveSyncState(mydocmaUid, faultUid, activityId, null, attachmentId, new SyncState({status: SyncStatusEnum.Syncing}));
        attachmentChangeState = this.patchService.generateChangeStateFromPatches(patches.filter(p => p.operation.path === FaultOperationPathEnum.activityAttachment));

        try {
          if (attachmentChangeState.status !== ChangeStatusEnum.NotChanged) {
            let newFile: File;
            if (attachmentChangeState.status === ChangeStatusEnum.New) {
              isNew = true;
              newFile = await this.getAttachmentFile(mydocmaUid, attachmentUid);
              if (!newFile) {
                // NO FILE EXISTING ANYMORE; REMOVE FAULT_PATCH
                await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));

                const m = `Can't sync new attachment ${attachmentId}. Sync file not found`;
                this.logger.error(m, '', 'AttachmentSyncService');
                throw new Error(m);
              }
            }

            const attachmentLoadedState = await this.syncActivityAttachmentChangeState(mydocmaUid, newFile, activityUid, attachmentUid, attachmentChangeState, (Object.keys(groupedScribblePatches)?.length ?? 0) > 0);
            attachmentLoadStates.push(attachmentLoadedState);
            await this.dbService.saveLoadState(mydocmaUid, faultUid, activityUid, null, attachmentUid, attachmentLoadedState);
          }
          await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));

          // const syncState = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, null, attachmentUid, new SyncState({status: SyncStatusEnum.Synced}));
          // return ({syncResult: new SyncResult({syncState, activityAttachmentsLoadStates: [loadedState]}), isNewAttachment: isNew});
        } catch (err) {
          this.logger.error(err, '', 'AttachmentSyncService');

          if (err.error === 'Unique constraint violation of \'UQ_FI_UNIQUE_ID\'') {
            // ATTACHMENT ALREADY UPLOADED, REMOVE FAULT_PATCH + FILE
            try {
              await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));
              await this.syncFileService.removeFile(mydocmaUid, attachmentUid);

              try {
                const attachmentData = await this.attachmentApiService.getAttachmentData(attachmentUid);
                const attachmentLoadedState = new LoadState<AttachmentData>({status: LoadStatusEnum.Loaded, data: attachmentData});
                attachmentLoadStates.push(attachmentLoadedState);
                await this.dbService.saveLoadState(mydocmaUid, faultUid, activityUid, null, attachmentUid, attachmentLoadedState);
              } catch (err3) {
                this.logger.error(err3, '', 'AttachmentSyncService');
              }

              // const syncState = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, null, attachmentUid, new SyncState({status: SyncStatusEnum.Synced}));
              // return ({syncResult: new SyncResult({syncState}), isNewAttachment: isNew});
            } catch (err2) {
              attachmentError = this.errorService.getError(err2);
              // console.error(error2Internal);
              //
              // const syncState = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, null, attachmentUid, new SyncState({status: SyncStatusEnum.Error, error: error2Internal}));
              // return ({syncResult: new SyncResult({syncState}), isNewAttachment: isNew});
            }
          } else if (err.error.Code === '1000017') { // Todo: add lock flag on Attachment to avoid error
            this.notifactionService.error(this.translateService.instant('MM.fault.lockedAttachment'));
            await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));
            await this.syncFileService.removeFile(mydocmaUid, attachmentUid);
            // return;
          } else {
            attachmentError = this.errorService.getError(err);
            // return ({syncResult: new SyncResult({syncState: await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, null, attachmentUid, new SyncState({status: SyncStatusEnum.Error, error: errorInternal}))}), isNewAttachment: isNew});
          }
        }
      }

      const scribbleChangeStates = Object.keys(groupedScribblePatches).map(scribbleUid => {
        const scribblePatches = groupedScribblePatches[scribbleUid];
        const scribbleChangeState = this.patchService.generateChangeStateFromPatches(scribblePatches);
        return {changeStates: scribbleChangeState, patches: scribblePatches, status: scribbleChangeState.status};
      });

      const processingScribbleUids = scribbleChangeStates.filter(x => x.changeStates.data.status !== ChangeStatusEnum.NotChanged).map(x => x.changeStates.data.UniqueId);
      await this.scribbleDbService.saveSyncState(mydocmaUid, faultUid, activityUid, null, attachmentUid, processingScribbleUids, new SyncState({status: SyncStatusEnum.Syncing}));

      const newScribbleChangeStatesPaged  = pageBySize<{changeStates: ChangeState<any>, patches: FaultPatch[], status: ChangeStatusEnum}>(scribbleChangeStates.filter(x => x.status === ChangeStatusEnum.New), 50);
      const updatedScribbleChangeStatesPaged  = pageBySize<{changeStates: ChangeState<any>, patches: FaultPatch[]}>(scribbleChangeStates.filter(x => x.status === ChangeStatusEnum.Changed), 50);
      const deletedScribbleChangeStatesPaged  = pageBySize<{changeStates: ChangeState<any>, patches: FaultPatch[]}>(scribbleChangeStates.filter(x => x.status === ChangeStatusEnum.Deleted), 50);

      for (const changeStates of [...newScribbleChangeStatesPaged, ...updatedScribbleChangeStatesPaged, ...deletedScribbleChangeStatesPaged]) {
        try {
          const loadedStates = await this.syncScribbleChangeStates(mydocmaUid, changeStates.map(x => x.changeStates));
          scribbleLoadStates.push(... loadedStates);
          await this.scribbleDbService.saveLoadStates(mydocmaUid, faultUid, activityUid, null, attachmentUid, loadedStates, changeStates[0].changeStates.status === ChangeStatusEnum.Deleted);
          await this.patchService.remove(mydocmaUid, changeStates.flatMap(x => x.patches.map(p => p.operation.id)));
        } catch (err) {
          const error = this.errorService.getError(err);
          scribbleErrors.push(error);
          this.logger.error(error, '', 'AttachmentSyncService');
        }
      }

      if (attachmentError) {
        const syncStateAttachmentErr = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityId, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Error, error: attachmentError }));
        return ({syncResult: new SyncResult({syncState: syncStateAttachmentErr}), isNewAttachment: isNew});
      } else if (scribbleErrors.length > 0) {
        const syncStateScribbleErr = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityId, null, attachmentUid, new SyncState({ status: SyncStatusEnum.Error, error: scribbleErrors[0] }));
        return ({syncResult: new SyncResult({syncState: syncStateScribbleErr}), isNewAttachment: isNew});
      }

      await this.scribbleDbService.saveSyncState(mydocmaUid, faultUid, activityUid, null, attachmentUid, processingScribbleUids, new SyncState({status: SyncStatusEnum.Synced}));

      const syncState = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityId, null, attachmentUid, new SyncState({status: SyncStatusEnum.Synced}));
      return ({syncResult: new SyncResult({syncState, activityAttachmentsLoadStates: attachmentLoadStates, activityAttachmentScribblesLoadStates: scribbleLoadStates}), isNewAttachment: isNew});
    }
    catch (err) {
      const syncStateErr = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityId, null, attachmentUid, new SyncState({status: SyncStatusEnum.Error, error: this.errorService.getError(err)}));
      return ({syncResult: new SyncResult({syncState: syncStateErr}), isNewAttachment: isNew});
    }
  }

  public async syncActivityGroupAttachmentFromPatches(mydocmaUid: string, groupUid: string, groupId: number, patches: {[p: string]: FaultPatch[]}, faultMap: Map<string, string>): Promise<SyncResult> {
    console.debug(`Syc activity group attachments`);
    const items: {faultUid: string, activityUid: string, attachmentUid: string}[] = [];
    const allPatches: FaultPatch[] = [];
    const syncResult = new SyncResult();

    for (const key of Object.keys(patches)) {
      const attachmentPatches: FaultPatch[] = patches[key];
      allPatches.push(... attachmentPatches);

      this.assertValidPatches(attachmentPatches, FaultOperationPathEnum.groupAttachment);
      const attachmentUid = attachmentPatches[0].oUid;
      // const groupIdInternal = attachmentPatches[0].gId || groupId;

      faultMap.forEach((fUid, aUid) => {
        items.push({faultUid: fUid, activityUid: aUid, attachmentUid});
      });
    }

    try {
      await this.dbService.saveSyncStates(mydocmaUid, groupUid, items, new SyncState({ status: SyncStatusEnum.Syncing }));

      for (const key of Object.keys(patches)) {
        const attachmentPatches: FaultPatch[] = patches[key];
        const attachmentChangeState = this.patchService.generateChangeStateFromPatches(attachmentPatches);
        if (attachmentChangeState.status !== ChangeStatusEnum.NotChanged) {
          /* upload attachments ... */
          const savedLoadState = await this.syncActivityGroupAttachmentChangeState(mydocmaUid,
                                                                                   groupUid,
                                                                                   attachmentPatches[0].oId,
                                                                                   attachmentPatches[0].oUid,
                                                                                   attachmentChangeState,
                                                                                   false);    // (Object.keys(groupedScribblePatches)?.length ?? 0) > 0

          syncResult.activityAttachmentsLoadStates.push(savedLoadState);

          const loadItems = items.filter(f => f.attachmentUid === attachmentPatches[0].oUid);
          await this.dbService.saveLoadStates(mydocmaUid, groupUid, loadItems, savedLoadState);
        }
      }

      syncResult.syncState = new SyncState({ status: SyncStatusEnum.Synced });
      await this.patchService.remove(mydocmaUid, allPatches.map(p => p.operation.id));
      await this.dbService.saveSyncStates(mydocmaUid, groupUid, items, syncResult.syncState);
      return syncResult;
    } catch (err) {
      this.logger.error(err, '', 'AttachmentSyncService');

      // if (err.error.Code === '1000017') { // Todo: add lock flag on Attachment to avoid error
      //   this.notifactionService.error( await  this.translateService.get('MM.fault.lockedAttachment').toPromise());
      //   await this.patchService.remove(mydocmaUid, allPatches.map(p => p.operation.id));
      //   return;
      // }

      const syncStateErr = await this.dbService.saveSyncStates(mydocmaUid, groupUid, items, new SyncState({ status: SyncStatusEnum.Error }));
      return new SyncResult({syncState: syncStateErr});
    }
  }

  private assertValidPatches(patches: FaultPatch[], path: FaultOperationPathEnum) {
    if (!patches && patches.length === 0) {
      throw new Error(`Can't sync attachment. Sync patches are empty.`);
    }
    const faultUid = patches[0].fUid;
    const oUid = patches[0].oUid;
    for (const p of patches) {
      if (p.fUid !== faultUid || p.oUid !== oUid || p.operation.path !== path) {
        throw new Error(`Can't sync attachment ${faultUid}/${oUid}. Sync patches not valid`);
      }
    }
  }

  private async syncFaultAttachmentChangeState(mydocmaUid: string, newFile: File, faultUId: string, attachmentUId: string,
                                               changeState: ChangeState<AttachmentData>, forceNoThumbnail: boolean = false): Promise<LoadState<AttachmentData>> {
    if (changeState.status === ChangeStatusEnum.New) {
      const loadedData = await this.attachmentApiService.addAttachmentToFault(faultUId, changeState.data, newFile, forceNoThumbnail).toPromise();
      await this.syncFileService.removeFile(mydocmaUid, changeState.data.UniqueId);
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: loadedData });
    } else if (changeState.status === ChangeStatusEnum.Changed) {
      const updatedData = await firstValueFrom(await this.attachmentApiService.updateAttachment(attachmentUId, changeState.data));
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: updatedData });

    }
  }

  private async syncActivityAttachmentChangeState(mydocmaUid: string, newFile: File, activityUId: string, attachmentUId: string,
                                                  changeState: ChangeState<AttachmentData>, forceNoThumbnail: boolean = false): Promise<LoadState<AttachmentData>> {
    if (changeState.status === ChangeStatusEnum.New) {
      const loadedData = await this.attachmentApiService.addAttachmentToActivity(activityUId, changeState.data, newFile, forceNoThumbnail).toPromise();
      await this.syncFileService.removeFile(mydocmaUid, changeState.data.UniqueId);
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: loadedData });
    } else if (changeState.status === ChangeStatusEnum.Changed) {
      const updatedData = await firstValueFrom(await this.attachmentApiService.updateAttachment(attachmentUId, changeState.data));
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: updatedData });
    }
  }

  private async syncActivityGroupAttachmentChangeState(mydocmaUid: string, groupUid: string, attachmentId: number, attachmentUId: string,
                                                       changeState: ChangeState<AttachmentData>, forceNoThumbnail: boolean = false): Promise<LoadState<AttachmentData>> {
    if (changeState.status === ChangeStatusEnum.New) {
      const file = await this.getAttachmentFile(mydocmaUid, changeState.data.UniqueId);
      if (!file) {
        throw new Error(`Can't sync new attachment ${changeState.data.UniqueId}. Sync file not found`);
      }
      const loadedData = await this.attachmentApiService.addAttachmentToGroup(groupUid, changeState.data, file, forceNoThumbnail).toPromise();
      await this.syncFileService.removeFile(mydocmaUid, changeState.data.UniqueId);
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: loadedData });
    } else if (changeState.status === ChangeStatusEnum.Changed) {
      const updatedData = await firstValueFrom(await this.attachmentApiService.updateAttachment(attachmentUId, changeState.data));
      return new LoadState<AttachmentData>({ status: LoadStatusEnum.Loaded, data: updatedData });

    }
  }

  private async syncScribbleChangeStates(mydocmaUid: string, changeStates: ChangeState<ScribbleData>[]): Promise<LoadState<ScribbleData>[]> {
    if (changeStates[0].status === ChangeStatusEnum.New) {
      const taskId = await this.scribbleApiService.createScribbles(changeStates.map(x => x.data)).toPromise();
      return changeStates.map(x => new LoadState<ScribbleData>({ status: LoadStatusEnum.Loaded, data: x.data }));
    }
    else if (changeStates[0].status === ChangeStatusEnum.Changed) {
      const taskId = await this.scribbleApiService.updateScribbles(changeStates.map(x => x.data)).toPromise();
      return changeStates.map(x => new LoadState<ScribbleData>({ status: LoadStatusEnum.Loaded, data: x.data }));
    }
    else if (changeStates[0].status === ChangeStatusEnum.Deleted) {
      const taskId = await this.scribbleApiService.deleteScribbles(changeStates.map(x => x.data)).toPromise();
      return changeStates.map(x => new LoadState<ScribbleData>({ status: LoadStatusEnum.Loaded, data: x.data }));
    }
  }

  private async getAttachmentFile(mydocmaUid: string, attachmentUid: string): Promise<File> {
    const entity = await this.syncFileService.getFile(mydocmaUid, attachmentUid);
    if (!entity) {
      return null;
    }
    return mapFileEntityToFile(entity);
  }
}

